if (!customElements.get('sf-accessory-package')) {
  class SplitflaskAccessoryPackage extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.productInfo = this.closest('product-info');
      this.productForm = this.productInfo?.querySelector('product-form');
      this.form = this.productForm?.querySelector('form');
      this.quantityBundles = this.productInfo?.querySelector('sf-quantity-bundles');
      this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      this.choiceInputs = Array.from(this.querySelectorAll('[data-sf-package-choice]'));
      this.colorInputs = Array.from(this.querySelectorAll('[data-sf-accessory-variant]'));
      this.colorPanel = this.querySelector('[data-sf-accessory-colors]');
      this.errorElement = this.querySelector('[data-sf-package-error]');
      this.statusElement = this.querySelector('[data-sf-package-status]');
      this.mainPriceElement = this.querySelector('[data-sf-main-price]');
      this.packageTotalElement = this.querySelector('[data-sf-package-total]');
      this.accessoryPriceElement = this.querySelector('[data-sf-accessory-price]');
      this.colorLabelElement = this.querySelector('[data-sf-accessory-color-label]');
      this.accessoryImages = Array.from(this.productInfo?.querySelectorAll(
        '.sf-accessory-package__image, .sf-accessory-discovery__image'
      ) || []);
      this.discoveryElements = Array.from(this.productInfo?.querySelectorAll(
        `[data-sf-accessory-discovery][data-sf-accessory-block-id="${this.dataset.accessoryBlockId}"]`
      ) || []);
      this.discoveryLinks = this.discoveryElements.filter((element) => element.matches('[data-sf-accessory-jump]'));
      this.mainPrice = Number(this.dataset.mainPrice || 0);
      this.accessoryDiscount = Math.min(100, Math.max(0, Number(this.dataset.accessoryDiscount || 0)));
      this.accessoryDiscountCode = String(this.dataset.accessoryDiscountCode || '').trim();
      this.currency = this.dataset.currency || 'USD';
      this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
      this.accessoryVariants = this.readJson('[data-sf-accessory-variants]');
      this.mainVariants = this.readProductVariants();
      this.bundleQuantity = 1;
      this.bundleDiscount = 0;
      this.bundleTotal = null;
      this.userSelectedAccessoryColor = false;
      this.error = false;

      if (!this.form || !this.choiceInputs.length || !this.accessoryVariants.length) return;

      this.syncQuantityBundleState();
      this.addEventListener('change', this.onChange);
      this.discoveryLinks.forEach((link) => link.addEventListener('click', this.onDiscoveryClick));
      this.productInfo?.addEventListener('sf:bundle-change', this.onQuantityBundleChange);
      this.form.addEventListener('submit', this.onFormSubmit, true);
      this.form.querySelector('[name="quantity"]')?.addEventListener('input', this.onQuantityChange);
      this.variantChangeUnsubscriber = typeof subscribe === 'function'
        ? subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange)
        : null;

      this.matchAccessoryToMainVariant();
      this.applySelection();
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.onChange);
      this.discoveryLinks?.forEach((link) => link.removeEventListener('click', this.onDiscoveryClick));
      this.productInfo?.removeEventListener('sf:bundle-change', this.onQuantityBundleChange);
      this.form?.removeEventListener('submit', this.onFormSubmit, true);
      this.form?.querySelector('[name="quantity"]')?.removeEventListener('input', this.onQuantityChange);
      if (this.variantChangeUnsubscriber) this.variantChangeUnsubscriber();
      if (this.discoveryFocusTimer) window.clearTimeout(this.discoveryFocusTimer);
      if (this.discoveryHighlightTimer) window.clearTimeout(this.discoveryHighlightTimer);
      this.initialized = false;
    }

    readJson(selector) {
      try {
        return JSON.parse(this.querySelector(selector)?.textContent || '[]');
      } catch (error) {
        console.warn('Splitflask accessory package data could not be read.', error);
        return [];
      }
    }

    readProductVariants() {
      try {
        const json = this.productInfo?.querySelector('[data-all-variants]')?.textContent;
        return json ? JSON.parse(json) : [];
      } catch (error) {
        return [];
      }
    }

    get packageSelected() {
      return this.querySelector('[data-sf-package-choice][value="package"]')?.checked === true;
    }

    get selectedAccessoryInput() {
      return this.querySelector('[data-sf-accessory-variant]:checked');
    }

    get selectedAccessoryVariant() {
      const selectedId = Number(this.selectedAccessoryInput?.value || 0);
      return this.accessoryVariants.find((variant) => Number(variant.id) === selectedId)
        || this.accessoryVariants.find((variant) => variant.available)
        || null;
    }

    get mainQuantity() {
      if (this.quantityBundles) {
        const bundleQuantity = Number(this.bundleQuantity || this.quantityBundles.currentQuantity || 1);
        return Math.max(1, Number.isFinite(bundleQuantity) ? bundleQuantity : 1);
      }

      const inputQuantity = Number(this.form?.querySelector('[name="quantity"]')?.value || 1);
      return Math.max(1, Number.isFinite(inputQuantity) ? inputQuantity : 1);
    }

    syncQuantityBundleState() {
      if (!this.quantityBundles) return;

      const selectedChoice = this.quantityBundles.querySelector('[data-sf-bundle-choice]:checked');
      const selectedTier = this.quantityBundles.querySelector(
        `[data-sf-bundle-tier][data-quantity="${selectedChoice?.value || 1}"]`
      );
      this.bundleQuantity = Math.max(1, Number(
        this.quantityBundles.currentQuantity || selectedTier?.dataset.quantity || 1
      ));
      this.bundleDiscount = Number(
        this.quantityBundles.currentDiscount ?? selectedTier?.dataset.discount ?? 0
      );

      const currentTotal = this.quantityBundles.currentTotal;
      this.bundleTotal = currentTotal == null
        ? Math.round(this.mainPrice * this.bundleQuantity * (100 - this.bundleDiscount) / 100)
        : Number(currentTotal);
    }

    onChange = (event) => {
      const choice = event.target.closest('[data-sf-package-choice]');
      if (choice) {
        this.applySelection();
        return;
      }

      const accessoryColor = event.target.closest('[data-sf-accessory-variant]');
      if (!accessoryColor) return;
      this.userSelectedAccessoryColor = true;
      const packageChoice = this.querySelector('[data-sf-package-choice][value="package"]');
      if (packageChoice && !packageChoice.checked) packageChoice.checked = true;
      this.applySelection();
    };

    onQuantityChange = () => this.updatePricesAndLabels();

    onDiscoveryClick = (event) => {
      event.preventDefault();
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.scrollIntoView({
        behavior: reduceMotion ? 'auto' : 'smooth',
        block: 'center',
      });

      this.classList.add('is-highlighted');
      if (this.discoveryFocusTimer) window.clearTimeout(this.discoveryFocusTimer);
      if (this.discoveryHighlightTimer) window.clearTimeout(this.discoveryHighlightTimer);

      const packageChoice = this.querySelector('[data-sf-package-choice][value="package"]');
      this.discoveryFocusTimer = window.setTimeout(() => {
        packageChoice?.focus({ preventScroll: true });
      }, reduceMotion ? 0 : 300);
      this.discoveryHighlightTimer = window.setTimeout(() => {
        this.classList.remove('is-highlighted');
      }, reduceMotion ? 500 : 1400);
    };

    onQuantityBundleChange = (event) => {
      this.bundleQuantity = Math.max(1, Number(event.detail?.quantity || 1));
      this.bundleDiscount = Number(event.detail?.discount || 0);
      this.bundleTotal = Number(event.detail?.total ?? this.mainPrice * this.bundleQuantity);
      this.updateDynamicCheckout(this.packageSelected);
      this.updatePricesAndLabels();
    };

    onVariantChange = (event) => {
      const detail = event?.data;
      if (!detail || String(detail.sectionId) !== String(this.dataset.sectionId) || !detail.variant) return;
      this.mainPrice = Number(detail.variant.price || 0);
      this.dataset.mainPrice = String(this.mainPrice);
      this.updateCupColor(detail.variant.options || []);
      if (!this.userSelectedAccessoryColor) this.matchAccessoryToOptions(detail.variant.options || []);
      this.updateAccessoryPresentation();
      this.updatePricesAndLabels();
    };

    updateCupColor(mainOptions) {
      const options = Array.isArray(mainOptions) ? mainOptions : [];
      this.discoveryElements.forEach((element) => {
        const colorLabel = element.querySelector('[data-sf-cup-color]');
        const optionIndex = Number(colorLabel?.dataset.optionIndex);
        const color = Number.isInteger(optionIndex) ? options[optionIndex] : '';
        if (colorLabel && color) colorLabel.textContent = color;
      });
    }

    matchAccessoryToMainVariant() {
      if (this.dataset.autoMatchColor !== 'true' || !this.colorInputs.length) return;
      const mainVariantId = Number(this.form?.querySelector('[name="id"]')?.value || 0);
      const mainVariant = this.mainVariants.find((variant) => Number(variant.id) === mainVariantId);
      if (mainVariant?.options) this.matchAccessoryToOptions(mainVariant.options);
    }

    matchAccessoryToOptions(mainOptions) {
      if (this.dataset.autoMatchColor !== 'true' || !this.colorInputs.length) return;
      const normalizedMainOptions = mainOptions.map((value) => String(value).trim().toLowerCase());
      const match = this.colorInputs.find((input) => (
        !input.disabled
        && normalizedMainOptions.includes(String(input.dataset.optionValue || '').trim().toLowerCase())
      ));
      if (match) match.checked = true;
    }

    applySelection() {
      const selected = this.packageSelected;
      if (this.colorPanel) this.colorPanel.hidden = !selected;
      this.choiceInputs.forEach((input) => {
        input.closest('.sf-accessory-package__choice')?.toggleAttribute('data-selected', input.checked);
      });
      this.updateDiscoveryState();
      this.updateAccessoryPresentation();
      this.updateDynamicCheckout(selected);
      this.updatePricesAndLabels();
      this.clearError();
    }

    updateAccessoryPresentation() {
      const variant = this.selectedAccessoryVariant;
      const selectedInput = this.selectedAccessoryInput;
      if (!variant) return;

      if (this.colorLabelElement) {
        this.colorLabelElement.textContent = selectedInput?.dataset.optionValue || variant.title;
      }
      const imageUrl = selectedInput?.dataset.variantImage || variant.image;
      if (imageUrl) {
        this.accessoryImages.forEach((image) => {
          image.removeAttribute('srcset');
          image.src = imageUrl;
        });
      }
    }

    updateDiscoveryState() {
      const selected = this.packageSelected;
      this.discoveryElements.forEach((element) => {
        element.toggleAttribute('data-selected', selected);
        element.querySelectorAll('[data-sf-discovery-state], [data-sf-discovery-action]').forEach((label) => {
          const text = selected ? label.dataset.selectedText : label.dataset.availableText;
          if (text) label.textContent = text;
        });
      });
    }

    getDiscountedAccessoryPrice(price) {
      const regularPrice = Number(price || 0);
      const discountAmount = Math.floor(regularPrice * this.accessoryDiscount / 100);
      return regularPrice - discountAmount;
    }

    updatePricesAndLabels() {
      const accessoryVariant = this.selectedAccessoryVariant;
      const accessoryPrice = Number(accessoryVariant?.price || 0);
      const discountedAccessoryPrice = this.getDiscountedAccessoryPrice(accessoryPrice);
      const mainQuantity = this.mainQuantity;
      const accessoryQuantity = this.dataset.accessoryQuantity === 'one' ? 1 : mainQuantity;
      const bundleTotal = Number(this.bundleTotal);
      const mainTotal = this.quantityBundles && this.bundleTotal != null && Number.isFinite(bundleTotal)
        ? bundleTotal
        : this.mainPrice * mainQuantity;
      const accessoryRegularTotal = accessoryPrice * accessoryQuantity;
      const accessoryTotal = discountedAccessoryPrice * accessoryQuantity;
      const packageTotal = mainTotal + accessoryTotal;

      if (this.mainPriceElement) this.mainPriceElement.textContent = this.formatMoney(mainTotal);
      if (this.accessoryPriceElement) this.accessoryPriceElement.textContent = this.formatMoney(accessoryTotal);
      const accessoryCompare = this.querySelector('[data-sf-accessory-compare]');
      if (accessoryCompare) {
        accessoryCompare.textContent = this.formatMoney(accessoryRegularTotal);
        accessoryCompare.hidden = this.accessoryDiscount <= 0;
      }
      if (this.packageTotalElement) this.packageTotalElement.textContent = this.formatMoney(packageTotal);

      this.discoveryElements.forEach((element) => {
        element.querySelectorAll('[data-sf-discovery-price]').forEach((price) => {
          price.textContent = `+${this.formatMoney(accessoryTotal)}`;
        });
        element.querySelectorAll('[data-sf-discovery-compare]').forEach((compare) => {
          compare.textContent = this.formatMoney(accessoryRegularTotal);
          compare.hidden = this.accessoryDiscount <= 0;
        });
      });

      const submitButton = this.productInfo?.querySelector('[id^="ProductSubmitButton-"]');
      const submitLabel = submitButton?.querySelector(':scope > span');
      if (submitLabel && !submitButton.disabled) {
        const label = this.packageSelected ? this.dataset.packageCtaLabel : this.dataset.cupCtaLabel;
        submitLabel.textContent = `${label} · ${this.formatMoney(this.packageSelected ? packageTotal : mainTotal)}`;
      }

      const stickyLabel = this.productInfo?.querySelector('.product-bar__submit span');
      if (stickyLabel) {
        stickyLabel.textContent = this.packageSelected ? this.dataset.packageCtaLabel : this.dataset.cupCtaLabel;
      }
    }

    updateDynamicCheckout(packageSelected) {
      const paymentButton = this.productInfo?.querySelector('.shopify-payment-button');
      if (!paymentButton) return;
      if (this.dynamicCheckoutInitiallyHidden === undefined) {
        this.dynamicCheckoutInitiallyHidden = paymentButton.hidden;
      }
      paymentButton.hidden = packageSelected || this.mainQuantity > 1
        ? true
        : this.dynamicCheckoutInitiallyHidden;
      paymentButton.setAttribute('aria-hidden', paymentButton.hidden ? 'true' : 'false');
    }

    formatMoney(cents) {
      if (typeof formatPrice === 'function') return formatPrice(cents, this.moneyFormat);
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency: this.currency,
      }).format(Number(cents || 0) / 100);
    }

    getMainItem() {
      const formData = new FormData(this.form);
      const item = {
        id: Number(formData.get('id')),
        quantity: this.mainQuantity,
      };
      const properties = {};

      formData.forEach((value, key) => {
        const propertyMatch = key.match(/^properties\[(.+)]$/);
        if (propertyMatch && value !== '') properties[propertyMatch[1]] = value;
      });

      if (this.productForm?.hasAttribute('data-has-pre-order')) properties['Pre-order'] = '✓';
      if (Object.keys(properties).length) item.properties = properties;

      const sellingPlan = Number(formData.get('selling_plan') || 0);
      if (sellingPlan) item.selling_plan = sellingPlan;
      return item;
    }

    getAccessoryCartItem(mainQuantity = this.mainQuantity) {
      if (!this.packageSelected) return null;
      const accessoryVariant = this.selectedAccessoryVariant;
      if (!accessoryVariant?.available) return null;

      return {
        id: Number(accessoryVariant.id),
        quantity: this.dataset.accessoryQuantity === 'one'
          ? 1
          : Math.max(1, Number(mainQuantity || 1)),
      };
    }

    async getExistingDiscountCodes() {
      try {
        const response = await fetch(`${routes.cart_url}.js`, {
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) return [];
        const cartData = await response.json();
        return (cartData.discount_codes || [])
          .map((discount) => typeof discount === 'string' ? discount : discount.code)
          .filter(Boolean);
      } catch (error) {
        return [];
      }
    }

    hasEquivalentPackageSavings(cartData) {
      const accessoryVariant = this.selectedAccessoryVariant;
      if (!accessoryVariant?.id || !Array.isArray(cartData?.items)) return false;

      const accessoryQuantity = this.dataset.accessoryQuantity === 'one' ? 1 : this.mainQuantity;
      const expectedSavings = Math.floor(
        Number(accessoryVariant.price || 0) * this.accessoryDiscount / 100
      ) * accessoryQuantity;
      if (expectedSavings <= 0) return true;

      const packageVariantIds = new Set([Number(accessoryVariant.id)]);
      const mainVariantId = Number(this.form?.querySelector('[name="id"]')?.value || 0);
      if (mainVariantId) packageVariantIds.add(mainVariantId);
      this.quantityBundles?.getItems?.(this.mainQuantity).forEach((item) => {
        if (item?.id) packageVariantIds.add(Number(item.id));
      });

      const appliedSavings = cartData.items
        .filter((item) => packageVariantIds.has(Number(item.variant_id || item.id)))
        .reduce((total, item) => total + Number(item.total_discount || 0), 0);

      return appliedSavings >= expectedSavings;
    }

    async applyBundleDiscount(fallbackCartData) {
      if (!this.accessoryDiscountCode || this.accessoryDiscount <= 0) return fallbackCartData;

      const existingCodes = await this.getExistingDiscountCodes();
      const discountCodes = Array.from(new Set([
        ...existingCodes,
        this.accessoryDiscountCode,
      ]));
      const body = { discount: discountCodes.join(',') };

      if (this.cart?.getSectionsToRender) {
        body.sections = this.cart.getSectionsToRender().map((section) => section.section);
        body.sections_url = window.location.pathname;
      }

      const updateUrl = routes.cart_update_url.endsWith('.js')
        ? routes.cart_update_url
        : `${routes.cart_update_url}.js`;
      const response = await fetch(updateUrl, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(body),
      });
      const cartData = await response.json();

      if (!response.ok || cartData.status || cartData.errors) {
        throw new Error(cartData.description || cartData.message || 'The bundle discount could not be applied.');
      }

      const appliedDiscount = (cartData.discount_codes || []).find(
        (discount) => String(discount.code || discount).toLowerCase() === this.accessoryDiscountCode.toLowerCase()
      );
      if (!appliedDiscount || appliedDiscount.applicable === false) {
        if (this.hasEquivalentPackageSavings(cartData)) return cartData;

        const competingCodes = (cartData.discount_codes || [])
          .filter((discount) => discount.applicable !== false)
          .map((discount) => String(discount.code || discount))
          .filter((code) => code && code.toLowerCase() !== this.accessoryDiscountCode.toLowerCase());
        const error = new Error('The bundle discount is not applicable to this cart.');
        error.code = 'SF_DISCOUNT_CONFLICT';
        error.hasCompetingPromotion = competingCodes.length > 0;
        throw error;
      }

      return cartData;
    }

    onFormSubmit = async (event) => {
      if (!this.packageSelected) return;
      if (Number(this.quantityBundles?.currentQuantity || this.bundleQuantity || 1) > 1) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      if (this.submitting) return;

      const accessoryVariant = this.selectedAccessoryVariant;
      if (!accessoryVariant?.available) {
        this.showError('Please choose an available accessory color.');
        return;
      }

      const mainItem = this.getMainItem();
      if (!mainItem.id) {
        this.showError('Please choose an available cup option.');
        return;
      }

      const accessoryItem = this.getAccessoryCartItem(mainItem.quantity);
      const items = [
        mainItem,
        accessoryItem,
      ];

      this.submitting = true;
      this.error = false;
      this.activeSubmitter = event.submitter;
      this.clearError();
      this.setLoading(true);
      if (this.statusElement) this.statusElement.textContent = 'Adding cup and accessory to cart';

      try {
        const body = { items };
        if (this.cart?.getSectionsToRender) {
          body.sections = this.cart.getSectionsToRender().map((section) => section.section);
          body.sections_url = window.location.pathname;
          this.cart.setActiveElement?.(document.activeElement);
        }

        const response = await fetch(routes.cart_add_url, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify(body),
        });
        let cartData = await response.json();

        if (!response.ok || cartData.status || cartData.errors) {
          const message = cartData.description || cartData.message || 'We could not add this package. Please try again.';
          this.error = true;
          this.showError(message);
          if (typeof publish === 'function') {
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'splitflask-accessory-package',
              productVariantId: mainItem.id,
              errors: cartData.errors || message,
              message,
            });
          }
          return;
        }

        if (this.accessoryDiscountCode && this.accessoryDiscount > 0) {
          if (this.statusElement) {
            this.statusElement.textContent = this.accessoryDiscount === 100
              ? 'Applying your free handle'
              : `Applying ${this.accessoryDiscount}% handle savings`;
          }
          try {
            cartData = await this.applyBundleDiscount(cartData);
          } catch (discountError) {
            this.error = true;
            const message = discountError?.hasCompetingPromotion
              ? 'Cup and handle were added, but another promotion cannot be combined with the free-handle offer. Please review your cart.'
              : 'Cup and handle were added, but the free-handle offer could not be applied. Please review your cart.';
            this.showError(message);
            if (this.cart) {
              this.cart.renderContents(cartData);
            } else {
              window.location = routes.cart_url;
            }
            console.error(discountError);
            return;
          }
        }

        if (!this.cart) {
          window.location = routes.cart_url;
          return;
        }

        if (typeof publish === 'function') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'splitflask-accessory-package',
            productVariantId: mainItem.id,
            cartData,
          });
        }
        if (this.statusElement) this.statusElement.textContent = 'Cup and accessory added to cart';
        this.cart.renderContents(cartData);
      } catch (error) {
        this.error = true;
        this.showError('We could not add this package. Please check your connection and try again.');
        console.error(error);
      } finally {
        this.setLoading(false);
        this.submitting = false;
        this.activeSubmitter = null;
      }
    };

    setLoading(isLoading) {
      const submitButton = this.productInfo?.querySelector('[id^="ProductSubmitButton-"]');
      const spinner = submitButton?.querySelector('.loading__spinner');
      [submitButton, this.activeSubmitter].filter(Boolean).forEach((button) => {
        button.classList.toggle('loading', isLoading);
        if (isLoading) {
          button.setAttribute('aria-disabled', 'true');
        } else if (!this.error && !button.disabled) {
          button.removeAttribute('aria-disabled');
        }
      });
      spinner?.classList.toggle('hidden', !isLoading);
      this.setExternalLoading(isLoading);
    }

    setExternalLoading(isLoading) {
      [...this.choiceInputs, ...this.colorInputs].forEach((input) => {
        input.disabled = isLoading || input.dataset.wasUnavailable === 'true';
      });
      this.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    }

    updateStatus(message) {
      if (this.statusElement) this.statusElement.textContent = message;
    }

    showError(message) {
      if (this.errorElement) {
        this.productForm?.handleErrorMessage?.();
        this.errorElement.textContent = message;
        this.errorElement.hidden = false;
      } else {
        this.productForm?.handleErrorMessage?.(message);
      }
      if (this.statusElement) this.statusElement.textContent = '';
    }

    clearError() {
      this.error = false;
      this.productForm?.handleErrorMessage?.();
      if (this.errorElement) {
        this.errorElement.textContent = '';
        this.errorElement.hidden = true;
      }
      if (this.statusElement) this.statusElement.textContent = '';
    }
  }

  customElements.define('sf-accessory-package', SplitflaskAccessoryPackage);
}
